import React from 'react'
import throttle from 'lodash.throttle'
import { cloneDeep } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

import { SocketContextType } from './types'

import { Routes } from '../../Routes'
import {
  hydrateMessage,
  timelineToMessages,
  timelineToNotes
} from '../../utilities'
import {
  createGroupObject,
  formatUpdateSession
} from '../../components/organisms/Conversations/helpers'
import { hydrateConversation } from '../../slices/convo/helpers'
import { getBulkSessions } from './helpers'
import {
  fetchPeerChatTimeline,
  fetchUserTimeline,
  fetchSessionTimeline,
  endSession,
  getSession,
  uploadFileEvent,
  getEnvironment,
  postStack,
  getStack,
  removeFromStack,
  updateSession,
  closeSession as closeSessionAPI
} from '../../lib/api'
import { selectAccess } from '../../slices/accessSlice'
import {
  addGroupChat,
  appendDMs,
  deleteMessage,
  filterDMs,
  handlePartKick,
  handleUpdateUsers,
  patchMessage,
  removeDM,
  selectDMs,
  setChatName,
  setChatType,
  setDMs,
  setQueueLoaded,
  setSelected,
  setType,
  updateDmMessages,
  updateDMs,
  updateDmSingleMessage,
  updateMessages as updateMessagesAction,
  updateMsgSeen,
  updateSingleDm
} from '../../slices/dms'
import { addIds } from '../../slices/sentMedia'
import {
  selectSocket,
  addEvent,
  startProcessing,
  failed,
  clearEvent,
  clearError
} from '../../slices/socket'
import {
  selectConvo,
  changeType,
  deleteConversation,
  updateConversations,
  setNew,
  setFetched,
  setConversation,
  endConversation,
  insertMessage,
  insertEvent,
  insertNote,
  trackUpdate,
  TOP_FILTERS,
  closeSession as closeSessionAction,
  updateConvoSingleMessage,
  updateConvoMessages,
  updateSingleCovo
} from '../../slices/convo'
import { selectBaseInbox, loadBaseInboxes } from '../../slices/baseInbox'
import {
  push as pushNotification,
  reload as reloadNotifications,
  loadUnread as loadUnreadNotifications,
  markRead as markNotificationRead
} from '../../slices/notification'
import { AddErrorAlert } from '../../slices/alert'
import useAccess, { PERMS } from '../../hooks/useAccess'
import { useAgent } from '../AgentProvider'
import { useAuth } from '../AuthProvider'
import { useUserAPI } from '../UserAPIProvider'
import { useTaskAPI } from '../TaskAPIProvider'
import { useSurvey } from '../SurveyProvider'
import { usePresence } from '../../hooks/usePresence'
import { useTyping } from '../TypingProvider'
import { useMediaLoaded } from '../MediaLoadedProvider'
import {
  useAppNotification,
  TYPES as NOTIFICATION_TYPES
} from '../AppNotificationProvider'
import { useReadStatus } from '../ReadStatusProvider'
import useWindowFocus from '../../hooks/useWindowFocus'
import { PAGE_SIZE, useTimelinePagination } from '../TimelinePaginationProvider'

export const SocketContext = React.createContext<SocketContextType>(null)

const SEND_TYPING_DELAY = 2500

export const CONVERSATION_FILTERS = {
  OPEN: 'Open',
  CLOSED_1_HR: 'Closed within 1 hr',
  CLOSED_3_HR: 'Closed within 3 hrs',
  CLOSED_1_DAY: 'Closed within 1 day',
  CLOSED_3_DAY: 'Closed within 3 days'
}

const PEERCHAT_FILTERS = {
  ALL: 'All Chats',
  PRIVATE: 'Private Only',
  GROUP: 'Group Only',
  CLOSED_GROUP: 'Closed Group'
}

const PAGE_TYPES = {
  DM: 'DMS',
  CHAT: 'CHATS'
}

const RETRY_TIMES = 2
const RETRY_SECONDS = 5000

const SocketProvider = ({ children }) => {
  // const history = useHistory()
  const dispatch = useAppDispatch()
  const { eventQueue, error, processing } = useAppSelector(selectSocket)
  const { loaded: accessLoaded } = useAppSelector(selectAccess)
  const {
    inbox: selectedInbox,
    topFilter,
    allConversations: conversations
  } = useAppSelector(selectConvo)
  const { loaded: baseInboxesLoaded } = useAppSelector(selectBaseInbox)

  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const { isLoggedIn, pathName } = useAuth()
  const {
    getUsers,
    propagateMerge: propagateMergeUsers,
    allUsers,
    handleEvent: handleUserEvent,
    loadUser
  } = useUserAPI()
  const { propagateMerge: propagateMergeTasks, handleEvent: handleTaskEvent } =
    useTaskAPI()
  const { handleEvent: handleSurveyEvent } = useSurvey()
  const {
    establishSession,
    disconnect,
    registerHandlers,
    deregisterHandlers,
    sendSessionMessage,
    sendTextMessage,
    sendEmail,
    sendSessionNote,
    sendSessionTypingEvent,
    joinSession,
    subscribeToInboxes,
    setUserSession,
    setUser,
    currentUser,
    updateRead
  } = useAgent()
  const { handleTypingEvent, unsetTyping } = useTyping()
  const showNotification = useAppNotification()
  const { presenceReceived, queryPresence } = usePresence()
  const { dms: rawDMs, chatName, queueLoaded } = useAppSelector(selectDMs)
  const dms = React.useMemo(() => cloneDeep(rawDMs), [rawDMs])

  const {
    handleUpdateRead,
    saveSessionsFromStack,
    handleOwnRead,
    setConvos,
    propagateMerge: propagateMergeRead
  } = useReadStatus()
  const { setDoneUploading } = useMediaLoaded()

  const hasPermission = useAccess()
  const { initialize: initPagination } = useTimelinePagination()
  const isFocused = useWindowFocus()

  const msgMap: SocketContextType['retry']['msgMap'] = React.useRef(new Map())
  const convoMap = React.useRef(new Map())

  // Conversations and direct messages come from sockets
  const [agents, setAgents] = React.useState<LocalUser[]>([])
  const [allAgents, setAllAgents] = React.useState<LocalUser[]>([])
  const [agentsSet, setAgentsSet] = React.useState(false)
  const [isClosingSession, setIsClosingSession] = React.useState(false)
  const [connected, setConnected] = React.useState(false)
  const [sentMessage, resetSentMessage] = React.useState(false)
  const [notFound, setNotFound] = React.useState<boolean>(false)
  const [initialConvosList, setInitialConvosList] = React.useState([])
  const [peerStack, setPeerStack] = React.useState({})
  const [allGroupChats, setAllGroupChats] = React.useState({})
  const exclude = React.useRef([])
  const [ownerQueue, setOwnerQueue] = React.useState([])

  // Loading states
  const [doneLoadingDMs, setDoneLoadingDMs] = React.useState(false)
  const [selectedPeerChatFilter, setSelectedPeerChatFilter] = React.useState(
    PEERCHAT_FILTERS.ALL
  )

  const canViewUnassigned = React.useMemo(
    () => hasPermission(PERMS.ConversationsActive),
    [hasPermission]
  )
  const canViewOwn = React.useMemo(
    () => hasPermission(PERMS.ConversationsBasic),
    [hasPermission]
  )

  // Sends typing event after a delay
  const throttledTypingEvent: SocketContextType['api']['throttledTypingEvent'] =
    // eslint-disable-next-line
    React.useCallback(
      throttle(
        (id, isUser) => sendSessionTypingEvent(id, isUser, allUsers),
        SEND_TYPING_DELAY,
        {
          trailing: false
        }
      ),
      [allUsers]
    )

  const retryCheck = React.useCallback(msg => {
    const messages = [...msgMap.current.values()]
    const index = messages.findIndex(
      obj => obj.payload.correlationId === msg.correlationId
    )

    if (!messages || !messages.length) {
      return
    } else if (index !== -1) {
      //found match, clear object for next message, only clear with the proper correlation id
      clearInterval(messages[index].timer)
      msgMap.current.delete(msg.correlationId)
      return true
    } else {
      return false
    }
  }, [])

  // Handles changing type
  const typeHandler: SocketContextType['api']['typeHandler'] =
    React.useCallback(
      (id, type, isDirect) => {
        if (isDirect) {
          dispatch(setType({ id, newType: type }))
        } else {
          dispatch(changeType({ id, type }))
        }
      },
      [dispatch]
    )

  // Ends a list of sessions
  const endSessions: SocketContextType['api']['endSessions'] =
    React.useCallback(
      async (ids, clear = false) => {
        setIsClosingSession(true)
        dispatch(clearError())

        if (clear) {
          dispatch(deleteConversation(ids))
          exclude.current = [...new Set([...exclude.current, ...ids])]
        }

        await Promise.all(
          ids.map(async id => {
            try {
              await endSession(id, conversations[id])
              dispatch(endConversation(id))
            } catch (err) {
              console.error('Error closing session', err)
              dispatch(AddErrorAlert({ err }))
            }
          })
        )
        setIsClosingSession(false)
      },
      [conversations, dispatch]
    )

  // Closes a single session
  const closeSession: SocketContextType['api']['closeSession'] =
    React.useCallback(
      async id => {
        try {
          // Close conversation in v1 and v2
          dispatch(closeSessionAction(id))
          dispatch(
            updateConversations(convs => {
              const newConv: LocalSession = {
                ...convs[id],
                state: 'CLOSED'
              }

              return { ...convs, [id]: newConv }
            })
          )

          // Close from api
          await closeSessionAPI(id, conversations[id])
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      },
      [conversations, dispatch]
    )

  // Immediately show sent message to timeline
  const showInTimeline = React.useCallback(
    payload => {
      const me = currentUser()?.name
      const { message, id, media, correlationId, isUser, expired } = payload

      // changing body of content according to channel
      let body
      if (!message.content) {
        body = message
      } else {
        body = message.content
      }

      let uid = id

      const updateMessages = list => {
        if (!(id in list)) {
          return list
        }

        const sender = me
        const date = new Date()

        const sideChannels = []
        if (Array.isArray(message.email)) {
          sideChannels.push(...message.email.map(id => ({ id })))
        } else {
          sideChannels.push({ id: message.email })
        }
        if (Array.isArray(message.number)) {
          sideChannels.push(...message.number.map(id => ({ id })))
        } else {
          sideChannels.push({ id: message.number })
        }

        const newMsg = {
          session_id: isUser ? null : id,
          user_id: isUser ? id : null,
          correlationId,
          content: body,
          media,
          senderFull: currentUser(),
          sender,
          tags: [],
          date,
          expired,
          type: 'chat',
          meta: {
            side_channels: sideChannels
          }
        }

        let updatedList
        // only hits here when sendSessionMessage returns 3 times
        if (expired) {
          const messages = [...list[uid].messages]
          const lastID = messages[messages.length - 1]?.correlationId

          if (msgMap.current.has(lastID)) {
            // looping through the retry messages with the end of the messages to find a match
            msgMap.current.forEach(msg => {
              for (let i = 0; i < messages.length; i++) {
                if (msg.payload.correlationId === messages[i].correlationId) {
                  let newPayload = {
                    session_id: id,
                    correlationId: msg.payload.correlationId,
                    content: msg.payload.message,
                    media: msg.payload.media,
                    senderFull: currentUser(),
                    sender,
                    tags: [],
                    expired,
                    date,
                    type: 'chat'
                  }
                  messages[i] = newPayload
                  break
                }
              }
            })
          }
          updatedList = messages
        } else {
          // update new message to list
          updatedList = [newMsg, ...list[uid].messages]
        }

        return {
          id: uid,
          messages: updatedList
        }
      }

      if (!isUser) {
        const obj = updateMessages(conversations)
        if (!obj[id]) return
        dispatch(updateConvoMessages({ id: obj.id, messages: obj.messages }))
      } else {
        // updating messages here
        const obj = updateMessages(dms)
        dispatch(updateDmMessages({ id: obj.id, messages: obj.messages }))
      }
    },
    [currentUser, dms, conversations, dispatch]
  )

  // Timer function that rehits message sending to server then display
  const startTimer: SocketContextType['retry']['startTimer'] =
    React.useCallback(
      id => {
        const payload = [...msgMap.current.values()]
        const index = payload.findIndex(msg => msg.payload.correlationId === id)

        // Payload hasn't updated yet
        if (index === -1) {
          return
        }

        if (payload[index].count !== RETRY_TIMES) {
          sendSessionMessage(payload[index].payload)
          payload[index].count += 1
          return
        }

        clearInterval(payload[index].timer)
        showInTimeline({
          ...payload[index].payload,
          expired: true,
          msgMap: msgMap
        })
      },
      [sendSessionMessage, showInTimeline]
    )

  // Sends a message
  const messageHandler: SocketContextType['api']['messageHandler'] =
    React.useCallback(
      async (message, mode, file, id, isDirect, group) => {
        dispatch(clearError())
        const uid = uuidv4()
        try {
          // create a promise that uploads all
          let media = []
          setDoneUploading(false)
          if (file?.length) {
            await Promise.all(
              file.map(async f => {
                try {
                  const media_res = await uploadFileEvent(f)
                  if (media_res) {
                    media.push(media_res.id)
                  }
                } catch (err) {
                  dispatch(AddErrorAlert({ err }))
                  setDoneUploading(true)
                }
              })
            )
          }
          dispatch(addIds(media))

          const list = isDirect ? dms : conversations
          let type = list[id].type

          const payload = {
            id,
            message,
            media,
            isUser: isDirect,
            correlationId: uid,
            group
          }

          // Join conversation if needed before sending
          if (
            !isDirect &&
            !conversations[id].users?.includes(currentUser()?.id)
          ) {
            await joinSession(id)
          }

          // starts timer and saves the timer to map obj
          if (['SMS', 'Live', 'Email'].includes(type)) {
            msgMap.current.set(payload.correlationId, {
              timer: setInterval(() => {
                startTimer(payload.correlationId)
              }, RETRY_SECONDS),
              'timer function': startTimer,
              payload,
              count: 0
            })
            showInTimeline(payload)
          }

          if (type === 'SMS') {
            await sendTextMessage(payload)
          } else if (type === 'Live') {
            await sendSessionMessage(payload)
          } else if (type === 'Email') {
            await sendEmail(payload)
          }
          setDoneUploading(true)

          // changes back to live in p2p if email/sms sent
          if (isDirect) {
            typeHandler(id, 'Live', isDirect)
          }

          if (!isDirect && mode === 'Send as Closed') {
            endSessions([id])
          }
        } catch (err) {
          console.error('Error sending socket message', err)
          dispatch(AddErrorAlert({ err }))
        }
      },
      [
        conversations,
        endSessions,
        sendSessionMessage,
        sendTextMessage,
        sendEmail,
        typeHandler,
        showInTimeline,
        startTimer,
        dms,
        currentUser,
        joinSession,
        setDoneUploading,
        dispatch
      ]
    )
  // Sends a message
  const messageHandlerV2: SocketContextType['api']['messageHandlerV2'] =
    React.useCallback(
      async (message, mode, file, id, isDirect, group) => {
        dispatch(clearError())
        const uid = uuidv4()
        try {
          // create a promise that uploads all
          let media = []
          setDoneUploading(false)
          if (file?.length) {
            await Promise.all(
              file.map(async f => {
                try {
                  const media_res = await uploadFileEvent(f)
                  if (media_res) {
                    media.push(media_res.id)
                  }
                } catch (err) {
                  dispatch(AddErrorAlert({ err }))
                  setDoneUploading(true)
                }
              })
            )
          }
          dispatch(addIds(media))

          const payload = {
            id,
            message,
            media,
            isUser: isDirect,
            correlationId: uid,
            group
          }

          // starts timer and saves the timer to map obj
          if (['SMS', 'Live', 'Email'].includes(mode)) {
            msgMap.current.set(payload.correlationId, {
              timer: setInterval(() => {
                startTimer(payload.correlationId)
              }, RETRY_SECONDS),
              'timer function': startTimer,
              payload,
              count: 0
            })
            showInTimeline(payload)
          }

          if (mode === 'SMS') {
            await sendTextMessage(payload)
          } else if (mode === 'Email') {
            await sendEmail(payload)
          }
          setDoneUploading(true)
        } catch (err) {
          console.error('Error sending socket message', err)
          dispatch(AddErrorAlert({ err }))
        }
      },
      [
        sendTextMessage,
        sendEmail,
        showInTimeline,
        startTimer,
        setDoneUploading,
        dispatch
      ]
    )

  // Submits a private note
  const noteHandler: SocketContextType['api']['noteHandler'] =
    React.useCallback(
      async (id, comment, mode, file) => {
        dispatch(clearError())
        try {
          let media = []
          if (file?.length) {
            await Promise.all(
              file.map(async f => {
                try {
                  const media_res = await uploadFileEvent(f)
                  if (media_res) {
                    media.push(media_res.id)
                  }
                } catch (err) {
                  dispatch(AddErrorAlert({ err }))
                  setDoneUploading(true)
                }
              })
            )
          }
          setDoneUploading(true)

          // Join conversation if needed before sending
          if (!conversations[id].users?.includes(currentUser()?.id)) {
            await joinSession(id)
          }

          sendSessionNote({ id, message: comment, media })

          if (mode === 'Send as Closed') {
            endSessions([id])
          }
        } catch (err) {
          console.error('Error uploading file in socket note', err)
          dispatch(AddErrorAlert({ err }))
        }
      },
      [
        endSessions,
        sendSessionNote,
        conversations,
        currentUser,
        joinSession,
        setDoneUploading,
        dispatch
      ]
    )

  // Resets the isNew status of a conversation or DM
  const markSeen: SocketContextType['api']['markSeen'] = React.useCallback(
    (id, isDirect) => {
      if (!id) {
        return
      }

      if (isFocused) {
        if (isDirect) {
          // dispatch read event
          if (allUsers[id]) {
            updateRead(id, null)
            handleOwnRead(id, null)
          } else {
            // dispatching read event from group chat
            handleOwnRead(null, id)
            updateRead(null, id)
          }

          dispatch(updateMsgSeen(id))
        } else {
          // dispatch read event
          const convo = conversations[id]
          const me = currentUser()?.id
          if (convo?.users?.includes(me)) {
            updateRead(null, id)
          }

          if (convo?.isNew) {
            dispatch(setNew(id))
          }
        }
      }
    },
    [
      updateRead,
      allUsers,
      handleOwnRead,
      conversations,
      currentUser,
      isFocused,
      dispatch
    ]
  )

  // Fetches the timeline for a conversation or dm
  const fetchTimeline: SocketContextType['api']['fetchTimeline'] =
    React.useCallback(
      async (id, isDirect, group) => {
        if (!id) {
          return
        }

        dispatch(clearError())

        if (isDirect && !group) {
          if (dms[id]?.fetched) {
            return
          }

          try {
            const { data, next } = await fetchPeerChatTimeline(id, PAGE_SIZE)

            initPagination(id, fetchPeerChatTimeline, next, {
              type: PAGE_TYPES.DM,
              id
            })
            const timeline = data
            const messages = timelineToMessages(timeline)

            dispatch(updateDMs({ id, fetched: true, messages }))
          } catch (err) {
            dispatch(updateDMs({ id, fetched: true, messages: [] }))
            dispatch(AddErrorAlert({ err }))
          }
        } else if (isDirect && group) {
          if (dms[id]?.fetched) {
            return
          }

          try {
            const { data, next } = await fetchSessionTimeline(id, PAGE_SIZE)
            initPagination(id, fetchSessionTimeline, next, {
              type: PAGE_TYPES.DM,
              id
            })
            const timeline = data
            const messages = timelineToMessages(timeline)

            dispatch(updateDMs({ id, fetched: true, messages }))
          } catch (err) {
            dispatch(updateDMs({ id, fetched: true, messages: [] }))
            dispatch(AddErrorAlert({ err }))
          }
        } else {
          if (conversations[id]?.fetched) {
            return
          }

          try {
            const owner_id = conversations[id]?.owner_id
            const session_id = conversations[id]?.session
            let timeline = []
            let messages = []
            let notes = []

            if (owner_id) {
              try {
                const { data, next } = await fetchUserTimeline(
                  owner_id,
                  PAGE_SIZE
                )
                initPagination(owner_id, fetchUserTimeline, next, {
                  type: PAGE_TYPES.CHAT,
                  id
                })
                timeline = data
              } catch (err) {
                dispatch(AddErrorAlert({ err }))
              }
            } else if (session_id) {
              try {
                const { data, next } = await fetchSessionTimeline(
                  session_id,
                  PAGE_SIZE
                )
                initPagination(session_id, fetchSessionTimeline, next, {
                  type: PAGE_TYPES.CHAT,
                  id
                })
                timeline = data
              } catch (err) {
                dispatch(AddErrorAlert({ err }))
              }
            }

            messages = timelineToMessages(timeline)
            notes = timelineToNotes(timeline)

            const newConv = {
              ...conversations[id],
              fetched: true,
              messages,
              notes
            }
            dispatch(updateSingleCovo({ id, dm: newConv }))
          } catch (err) {
            // Temporary fix for fetching timelines of sessions you are not in
            const ax = err.axiosError
            if (ax?.response?.status === 403) {
              const newConv = {
                ...conversations[id],
                fetched: true,
                messages: [],
                notes: []
              }
              dispatch(updateSingleCovo({ id, dm: newConv }))
            } else {
              console.error('Caught error fetching timeline', err)
              dispatch(AddErrorAlert({ err }))
            }
          }
        }
      },
      [conversations, dms, initPagination, dispatch]
    )

  // Fetches and saves messages for a single session
  const fetchSingleSession: SocketContextType['api']['fetchSingleSession'] =
    React.useCallback(
      async id => {
        try {
          const { data, next } = await fetchSessionTimeline(id)
          initPagination(id, fetchSessionTimeline, next, {
            type: PAGE_TYPES.CHAT,
            id
          })

          dispatch(
            updateConversations(old => {
              const updated = { ...old[id], fetched: true }
              updated.messages = timelineToMessages(data)
              updated.notes = timelineToNotes(data)

              return { ...old, [id]: updated }
            })
          )
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      },
      [initPagination, dispatch]
    )

  // Mocks a fetch being completed
  const finishFetch: SocketContextType['api']['finishFetch'] =
    React.useCallback(
      (id, isDirect) => {
        if (!isDirect) {
          dispatch(setFetched({ id, fetched: true }))
        }
      },
      [dispatch]
    )

  // Formats user DM for storage
  const formatDM: SocketContextType['peerChat']['formatDM'] = React.useCallback(
    async inUser => {
      if (!inUser) {
        return null
      }

      const { name, id, created_at, updated_at, latest_activity } = inUser

      // Load timeline if latest_activity doesn't exist
      let timeline = latest_activity
      if (!timeline) {
        try {
          const { data, next } = await fetchPeerChatTimeline(id, PAGE_SIZE)
          initPagination(id, fetchPeerChatTimeline, next, {
            type: PAGE_TYPES.DM,
            id
          })
          timeline = data
        } catch (err) {
          console.error(err)
          timeline = []
        }
      }
      const fetchedMessages = timelineToMessages(timeline)

      // Change with last activity
      const messages = timeline?.messages || fetchedMessages
      const user = { ...inUser }

      // Change when last activity exists
      const type = 'Live'

      const newSession = {
        name,
        type,
        messages,
        user,
        isNew: false,
        comments: [],
        fetched: false,
        session: id,
        startDate: created_at,
        lastUpdate: updated_at,
        users: [id, currentUser()?.id],
        closed: false,
        latest_activity
      }
      return newSession
    },
    [currentUser, initPagination]
  )

  // Queues a session to eventually have it's owner set
  const queueSetOwner = React.useCallback((id: string, owner: string) => {
    setOwnerQueue(old => {
      if (old.some(item => item.id === id)) {
        return old
      }

      return [...old, { id, owner }]
    })
  }, [])

  // Formats a session for storage
  const formatConversation: SocketContextType['api']['formatConversation'] =
    React.useCallback(
      (session, isNew) => {
        if (!session) {
          return null
        }

        return hydrateConversation(session, isNew, queueSetOwner)
      },
      [queueSetOwner]
    )

  // Adds a conversation to the list
  const pushConversation: SocketContextType['api']['pushConversation'] =
    React.useCallback(
      (id, newConversation, force = false) => {
        if (!newConversation.ended_at || force) {
          dispatch(setConversation({ id, conversation: newConversation }))
        }
      },
      [dispatch]
    )

  // Resets timeline to emulate merge
  const mergeTimeline: SocketContextType['api']['mergeTimeline'] =
    React.useCallback(
      async (sessionId, verifiedUserInfo, mergeInfo) => {
        try {
          const session = await getSession(sessionId)
          const augmentedSession = { ...session, owner_id: verifiedUserInfo.id }
          const newSession = await formatConversation(augmentedSession, false)

          if (!newSession) {
            return
          }

          dispatch(
            updateConversations(old => ({
              ...old,
              [augmentedSession.id]: {
                ...newSession,
                mergeInfo: [...(old[sessionId].mergeInfo ?? []), mergeInfo]
              }
            }))
          )
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
          console.error('Error merging timeline', err)
        }
      },
      [formatConversation, dispatch]
    )

  // Clears merge status (stops banner from showing)
  const clearMerge: SocketContextType['api']['clearMerge'] = React.useCallback(
    (sessionId, fromUserId, toUserId) => {
      dispatch(
        updateConversations(old => {
          const { mergeInfo, ...rest } = old[sessionId]
          const newMergeInfo = mergeInfo.filter(
            ({ from, to }) => from.id !== fromUserId && to.id !== toUserId
          )

          return {
            ...old,
            [sessionId]: {
              ...rest,
              mergeInfo: newMergeInfo,
              fetched: false
            }
          }
        })
      )
    },
    [dispatch]
  )

  // Joins a session
  const acceptConversation: SocketContextType['api']['acceptConversation'] =
    React.useCallback(
      async (id, join = true) => {
        if (join) {
          await joinSession(id)
        }

        let session
        setNotFound(false)
        try {
          const initialSession = conversations[id]

          if (!join) {
            if (initialSession) {
              session = initialSession
            } else {
              session = await getSession(id)
            }
          } else {
            const addAssignedTo = formatUpdateSession(
              initialSession,
              Boolean(initialSession.ended_at)
            )
            addAssignedTo.assigned_to = currentUser()?.id
            // @ts-ignore
            session = await updateSession(id, addAssignedTo)
          }
        } catch (err) {
          console.error('Error updating or formatting', err)
          setNotFound(true)
          // Needed to show empty timelines from 'go to timeline'
          pushConversation(id, {}, true)
          return
        }

        const newSession = await formatConversation(session, false)

        if (newSession) {
          newSession.fetched = conversations[id]?.fetched ?? false
          pushConversation(id, newSession, true)

          // add session to convos in read status
          let convos = {}
          const { id: sessionId, userIds, read_status, createdAt } = newSession

          convos[sessionId] = {}
          userIds.forEach(id => {
            convos[sessionId][id] = new Date(
              read_status.find(obj => obj.user_id === id)?.stamp ?? createdAt
            )
          })

          setConvos(convs => ({ ...convs, ...convos }))
        }
      },
      [
        formatConversation,
        pushConversation,
        joinSession,
        currentUser,
        conversations,
        setConvos
      ]
    )

  // Removes a conversation from your queue
  const rejectConversation: SocketContextType['api']['rejectConversation'] =
    React.useCallback(
      async (id, close = true) => {
        dispatch(clearError())

        // update convo and close
        try {
          dispatch(deleteConversation([id]))

          let initialSession = await getSession(id)
          const addAssignedTo = formatUpdateSession(initialSession, close)
          addAssignedTo.assigned_to = null
          // @ts-ignore
          await updateSession(id, addAssignedTo)
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      },
      [dispatch]
    )

  // Removes a conversation from your queue
  const denyConversation: SocketContextType['api']['denyConversation'] =
    React.useCallback(
      async (id, close = true) => {
        dispatch(clearError())

        // update convo and close
        try {
          dispatch(deleteConversation([id]))

          let initialSession = await getSession(id)
          const addAssignedTo = formatUpdateSession(initialSession, close)
          addAssignedTo.state = 'REJECTED'
          addAssignedTo.assigned_to = null
          // @ts-ignore
          await updateSession(id, addAssignedTo)
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      },
      [dispatch]
    )

  // Just rejects a conversation
  const denyConversationV2 = React.useCallback(
    async id => {
      dispatch(clearError())

      try {
        const initialSession = await getSession(id)
        const addAssignedTo = formatUpdateSession(initialSession, true)
        addAssignedTo.state = 'REJECTED'
        // @ts-ignore
        await updateSession(id, addAssignedTo)
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  // Removes a peer chat from queue
  const rejectDM: SocketContextType['api']['rejectDM'] = React.useCallback(
    id => {
      setNewPush(old => old.filter(session => session !== id))
      dispatch(removeDM(id))

      setAllGroupChats(old => ({
        ...old,
        [id]: {
          ...dms[id],
          latest_activity: dms[id].messages ?? dms[id].latest_activity ?? [],
          userIds: dms[id].users ?? [],
          id,
          converted: true
        }
      }))
    },
    [dispatch, dms]
  )

  // call create session and store
  const addGroupSession: SocketContextType['peerChat']['addGroupSession'] =
    React.useCallback(
      async (users, self) => {
        try {
          const sessionObj = await createGroupObject(users, self.id, chatName)

          if (!sessionObj) {
            return undefined
          }

          const { id, name } = sessionObj

          // resetting name after create
          dispatch(setChatName(''))
          dispatch(setChatType('Private Chat'))

          const newSession = {
            name: name,
            type: 'Live',
            messages: [],
            user: null,
            isNew: false,
            isGroup: true,
            comments: [],
            fetched: false,
            session: id,
            startDate: new Date().toISOString(),
            lastUpdate: new Date().toISOString(),
            users: [...users.map(u => u.id), self.id],
            closed: false
          }
          dispatch(setSelected(id))
          setNewPush(old => old.filter(o => o !== id))
          // @ts-ignore
          dispatch(setDMs({ id, session: newSession, isGroup: true }))

          const payload = {
            id: id,
            type: 'session'
          }

          await postStack(payload, 'default')
          return id
        } catch (err) {
          console.error('Error adding group session', err)
          return undefined
        }
      },
      [dispatch, chatName]
    )

  // Callback for when a socket connects
  const onConnect = React.useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('session_user') ?? '{}')
    const token = await getAccessTokenSilently()
    // Store session info
    setUserSession(user, token)

    // Listen to updates for session lists
    // TODO: Remove this hardcode later
    setConnected(true)
    const ids = ['00000000-0000-4b00-8157-000000000001']

    const timer = setInterval(() => {
      const success = subscribeToInboxes(ids)

      if (success) {
        clearInterval(timer)
      }
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [setUserSession, getAccessTokenSilently, subscribeToInboxes])

  // Handles socket disconnection
  const onDisconnect = React.useCallback(event => {}, [])

  // Handles errors in socket
  const onError = React.useCallback(
    async err => {
      // Redirect to login on error creating session
      console.error(`Error in ${pathName}`, err)

      if (
        ['eSendToUnvalidatedChannel', 'eSendToNonCompliantChannel'].includes(
          err.error_code
        )
      ) {
        const obj = err.error_message.split(',').splice(0, 1)

        const errorObj = {
          message: obj
        }
        dispatch(failed(errorObj))
        dispatch(AddErrorAlert(obj))
        return
      }

      if (err.name === 'AuthError') {
        dispatch(AddErrorAlert('Invalid session'))
        window.location.href = `/login/?last=${pathName}`
        return
      }

      if (err.name === 'eAuthExpired') {
        //if user is authenticated but got session expired, try connecting with new token.
        if (isAuthenticated) {
          disconnect()
          await connect()
          return
        }
        console.error('Session expired in socket provider.')

        dispatch(AddErrorAlert('Session expired'))

        window.location.href = `/logout`
        return
      }

      // Default show errors
      const { error_message: message = '' } = err
      const msg = message.split(',')[0]
      dispatch(failed(msg))
      dispatch(AddErrorAlert(msg))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathName, dispatch, isAuthenticated]
  )

  // Handles creating a socket session
  const connect = React.useCallback(async () => {
    const token = await getAccessTokenSilently()
    localStorage.setItem('session_token', token)
    establishSession({ token, onConnect, onError, onDisconnect })
  }, [
    getAccessTokenSilently,
    onConnect,
    onError,
    onDisconnect,
    establishSession
  ])

  // Handles a user joining a session
  const handleJoinEvent = React.useCallback(
    async msg => {
      const { content, sessionId, id } = msg
      const { user } = content

      if (dms[sessionId]) {
        const updateMessages = list => {
          const date = new Date()

          const newMsg = {
            content: msg.content.body,
            media: msg.content.media,
            sender: msg.content.user.name,
            senderFull: msg.content.user,
            meta: content.meta,
            session_id: sessionId,
            tags: [],
            type: 'join',
            id,
            date
          }

          // update new message to list
          const updatedList = [newMsg, ...list[sessionId].messages]

          return {
            ...list,
            [sessionId]: {
              ...list[sessionId],
              messages: updatedList,
              users: [...new Set([...list[sessionId].users, content.user.id])]
            }
          }
        }

        const obj = await updateMessages(dms)
        dispatch(updateMessagesAction(obj))

        // only notify when it's not yourself
        const newConvo = { ...dms[sessionId] }

        showNotification({
          muteOn: Routes.PEER_CHAT,
          type: NOTIFICATION_TYPES.GROUP,
          message: <i>{user.name} joined the conversation</i>,
          id,
          users: newConvo.users,
          name: newConvo.name,
          msg
        })
      } else {
        // Update sessions by adding this user to the conversation if needed
        dispatch(
          updateConversations(convos => {
            if (!(sessionId in convos)) {
              return convos
            }

            // Add the join event to the timeline
            const newConvo = {
              ...convos[sessionId],
              messages: [
                ...(convos[sessionId].messages ?? []),
                ...timelineToMessages([msg])
              ]
            }

            if (!newConvo.users.includes(user.id)) {
              newConvo.users = [...newConvo.users, user.id]
              setNewPush(old => [...old, sessionId])
            }

            showNotification({
              muteOn: Routes.CLIENT_CHATS,
              type: NOTIFICATION_TYPES.CONVERSATION,
              message: <i>{user.name} has joined the conversation</i>,
              id,
              users: newConvo.users,
              name: newConvo.name,
              msg
            })

            return { ...convos, [sessionId]: newConvo }
          })
        )

        dispatch(insertEvent(msg))
      }
    },
    [showNotification, dms, dispatch]
  )

  const formatGroupChat: SocketContextType['peerChat']['formatGroupChat'] =
    React.useCallback(
      async (c, isNew = true) => {
        const {
          name,
          id,
          userIds,
          owner_id,
          latest_activity,
          createdAt,
          updatedAt,
          created_by,
          users
        } = c

        if (owner_id) {
          acceptConversation(id, false)
          return false
        }

        // Fetch timeline if latest_activity does not exist
        let timeline = latest_activity
        if (!timeline) {
          try {
            const { data, next } = await fetchSessionTimeline(id, PAGE_SIZE)
            initPagination(id, fetchSessionTimeline, next, {
              type: PAGE_TYPES.DM,
              id
            })
            timeline = data
          } catch (err) {
            dispatch(AddErrorAlert({ err }))
            timeline = []
          }
        }
        const messages = timelineToMessages(timeline)

        const newSession = {
          ...c,
          name,
          type: 'Live',
          messages: messages,
          user: null,
          isNew: isNew,
          comments: [],
          fetched: false,
          session: id,
          startDate: createdAt ?? new Date().toISOString(),
          lastUpdate: updatedAt ?? new Date().toISOString(),
          users: userIds,
          closed: false,
          latest_activity,
          created_by,
          owner_id,
          fullUsers: users
        }

        dispatch(setDMs({ id, session: newSession, isGroup: true }))

        return true
      },
      [acceptConversation, initPagination, dispatch]
    )

  // add agent to DMs after delete
  const addAgent: SocketContextType['peerChat']['addAgent'] = React.useCallback(
    async (user, isNew = true) => {
      const newSession = await formatDM(user)

      if (newSession) {
        // retrigger message notification
        newSession.isNew = isNew
        // @ts-ignore
        dispatch(setDMs({ id: user.id, session: newSession, isGroup: false }))
      }
    },
    [formatDM, dispatch]
  )

  const [newPush, setNewPush] = React.useState<string[]>([])

  // Handles message from chat or sms
  const handleMessageEvent: SocketContextType['api']['handleMessageEvent'] =
    React.useCallback(
      msg => {
        let {
          action_type,
          content,
          sessionId,
          userId,
          id,
          tags,
          correlationId
        } = msg

        if (Array.isArray(content.media)) {
          dispatch(addIds(content.media.map(m => m.id)))
        }

        if (
          msgMap.current.has(correlationId) &&
          !msgMap.current.get(correlationId).sender
        ) {
          msgMap.current.set(correlationId, {
            ...msgMap.current.get(correlationId),
            sender: content?.user ?? currentUser()
          })
        }

        const isGroup = dms[msg.sessionId]
        const clientConvo = conversations[msg.sessionId]

        // if direct message add convo to map
        if (!msg.sessionId || !isGroup) {
          resetSentMessage(true)
          // setting key to either session or user id
          if (!msg.sessionId) {
            const msgUserID = msg.content.user?.id
            if (!dms[msgUserID] && msgUserID !== currentUser()?.id) {
              addAgent(msg.content.user)

              showNotification({
                muteOn: Routes.PEER_CHAT,
                type: NOTIFICATION_TYPES.PEER,
                message: msg.content.body,
                id,
                users: [msgUserID].filter(Boolean),
                name: msg.content.user?.name,
                msg
              })

              setNewPush(old => [...old, msg.content.user?.id])
            }
          } else {
            // need to add chat back to ref
            const formatSession = async () => {
              const session = await getSession(msg?.sessionId)
              const wasGroup = formatGroupChat(session)
              setNewPush(old => [...old, msg?.sessionId])
              wasGroup &&
                showNotification({
                  muteOn: Routes.PEER_CHAT,
                  type: NOTIFICATION_TYPES.GROUP,
                  message: msg.content.body,
                  id: msg.id,
                  users: session.userIds,
                  name: session.name,
                  msg
                })
            }
            // don't reformat group chat if session is with client
            if (!clientConvo) {
              formatSession().catch(err => {
                console.error('Error formatting session', err)
              })
            }
          }
        }

        let senderObj =
          content.user ?? msgMap.current.get(correlationId)?.sender

        let sender =
          senderObj?.name ?? msgMap.current.get(correlationId)?.sender.name

        let uid
        if (sessionId) {
          uid = sessionId
        } else {
          // checking if self or agent sending message
          if (currentUser()?.id === senderObj?.id) {
            uid = userId
          } else {
            uid = senderObj?.id
          }
        }
        const notifData = {
          message: content.body,
          id,
          msg
        }
        let type
        let muteOn = Routes.PEER_CHAT

        const updateMessages = (list, isPeerChat = false) => {
          if (!(uid in list)) {
            return list
          }

          const date = new Date(msg.stamp)
          const newMsg = {
            content: content.body,
            media: content.media,
            sender,
            senderFull: senderObj,
            meta: content.meta,
            session_id: sessionId,
            tags,
            type: action_type,
            id,
            date
          }

          let index
          let found

          if (correlationId && currentUser()?.id === senderObj?.id) {
            found = retryCheck(msg)
            // search for index of message to replace
            if (found) {
              index = list[uid].messages.findIndex(
                o => o.correlationId === correlationId
              )
              newMsg.date = list[uid].messages[index]?.date
            }
          }

          // replace message with message from server
          if (correlationId && index !== -1) {
            //Not sure why this is happening for outbound conversations.
            if (!isPeerChat && index === undefined) return
            // another agent sending message - with correlation id but no index
            let updateSingleMessage = true
            let toUpdate
            let isNew = false
            // Check for senderObj to avoid adding messages without a sender
            if (senderObj) {
              if (currentUser()?.id !== senderObj.id) {
                toUpdate = [
                  {
                    ...newMsg,
                    correlationId: correlationId,
                    received: true
                  },
                  ...list[uid].messages
                ]
                isNew = true
                updateSingleMessage = false
              }
              // agent sent message to same session from another device.
              else if (
                currentUser()?.id === senderObj.id &&
                index === undefined
              ) {
                toUpdate = [
                  {
                    ...newMsg,
                    correlationId: correlationId,
                    received: true
                  },
                  ...list[uid].messages
                ]
                updateSingleMessage = false
              } else {
                // agent sending the message
                toUpdate = {
                  ...newMsg,
                  correlationId: correlationId,
                  received: true
                }
              }
            }

            let update = {
              ...list[uid],
              isNew
            }

            if (isNew) {
              showNotification({
                ...notifData,
                users: update.users,
                name: update.name,
                type,
                muteOn,
                forceAudio: !isFocused
              })
            }

            if (updateSingleMessage) {
              if (isPeerChat) {
                dispatch(
                  updateDmSingleMessage({ id: uid, index, message: toUpdate })
                )
              } else {
                dispatch(
                  updateConvoSingleMessage({
                    id: uid,
                    index,
                    message: toUpdate
                  })
                )
              }
            } else {
              const keys = new Map()
              const unique = [...toUpdate].filter(item => {
                if (keys.has(item.id)) {
                  return false
                }

                keys.set(item.id, true)
                return true
              })
              if (isPeerChat) {
                dispatch(updateDmMessages({ id: uid, messages: unique }))
              } else {
                dispatch(updateConvoMessages({ id: uid, messages: unique }))
              }
            }
          } else {
            let update
            if (!found && correlationId) {
              return
            } else {
              update = {
                ...list[uid],
                messages: [
                  { ...newMsg, date: new Date() },
                  ...list[uid].messages
                ],
                isNew: true
              }
              showNotification({
                ...notifData,
                users: update.users,
                name: update.name,
                type,
                muteOn,
                forceAudio: !isFocused
              })
              if (isPeerChat) {
                dispatch(updateSingleDm({ id: uid, dm: update }))
              } else {
                dispatch(updateSingleCovo({ id: uid, dm: update }))
              }
            }
          }
        }

        if (sessionId) {
          const isGroup = dms[sessionId]

          type = isGroup
            ? NOTIFICATION_TYPES.GROUP
            : NOTIFICATION_TYPES.CONVERSATION
          if (!isGroup) {
            muteOn = Routes.CLIENT_CHATS
          }

          if (isGroup) {
            updateMessages(dms, true)
          } else {
            updateMessages(conversations, false)
            dispatch(insertMessage(msg))
          }
        } else {
          type = NOTIFICATION_TYPES.PEER
          // updating messages here
          updateMessages(dms, true)
        }

        if (content?.user?.id && content?.user?.name) {
          unsetTyping(uid, content.user.id, content.user.name)
        }
      },
      [
        retryCheck,
        currentUser,
        unsetTyping,
        formatGroupChat,
        showNotification,
        setNewPush,
        addAgent,
        dispatch,
        conversations,
        dms,
        isFocused
      ]
    )

  // Handles new note created event
  const handleNoteEvent = React.useCallback(
    msg => {
      const { content, sessionId, stamp, id, tags } = msg
      const uid = sessionId
      dispatch(
        updateConversations(convs => {
          if (!(uid in convs)) {
            return convs
          }

          const newNote = {
            content: content.body,
            sender: content.user.name,
            date: new Date(stamp),
            media: content.media,
            id,
            tags,
            senderFull: content.user,
            session_id: uid
          }

          return {
            ...convs,
            [uid]: {
              ...convs[uid],
              notes: [newNote, ...convs[uid].notes]
            }
          }
        })
      )

      dispatch(insertNote(msg))
    },
    [dispatch]
  )

  // Handles a subscribed list updating
  const handleInboxUpdateEvent = React.useCallback(
    async msg => {
      const { action, sessions: ids } = msg.content
      if (action === 'removed') {
        // Caused by something like a session moving between inboxes, unhandled for now
        return
      }

      // Handle update/added actions
      getBulkSessions(ids).then(sessions => {
        sessions.forEach(async session => {
          // Check if a session was ended or if it's a new session
          // Closed is set by becoming rejected as well
          if (session.ended_at || session.closed_at) {
            dispatch(
              updateConversations(conv => {
                if (!(session.id in conv)) {
                  return conv
                } else {
                  // if yours then add convo, or else remove
                  if (session.userIds.includes(currentUser()?.id)) {
                    return {
                      ...conv,
                      [session.id]: {
                        ...conv[session.id],
                        endDate: session.ended_at
                      }
                    }
                  } else {
                    return {
                      ...conv,
                      [session.id]: {
                        ...conv[session.id],
                        endDate: session.ended_at,
                        state: session.state
                      }
                    }
                  }
                }
              })
            )
          } else if (session.profile === 'PRIVATE') {
            try {
              const wasGroup = await formatGroupChat(session, true)
              if (wasGroup) {
                showNotification({
                  muteOn: Routes.PEER_CHAT,
                  type: NOTIFICATION_TYPES.GROUP,
                  id: msg.id,
                  users: session.userIds,
                  name: session.name,
                  msg
                })
              }
              setNewPush(old => [...old, session.id])
            } catch (err) {
              console.error('Error getting private session', err)
            }
          } else {
            const { state, userIds, id, initiated_by } = session
            let messages = []
            if (initiated_by === 'sms') {
              try {
                const { data } = await fetchSessionTimeline(id)
                messages = timelineToMessages(data) || []
              } catch (err) {
                dispatch(AddErrorAlert({ err }))
              }
            }
            //Store udpated session to hide join notifications
            dispatch(
              trackUpdate({
                id,
                conversation: { ...session, messages: [...messages] }
              })
            )

            // Remove from queue if current user isn't part of active
            if (
              state === 'ACTIVE' &&
              id in conversations &&
              !userIds.includes(currentUser()?.id)
            ) {
              dispatch(deleteConversation([id]))
              return
            }

            if (!canViewUnassigned) {
              return
            }

            const newSession = await formatConversation(session, false)

            setNewPush(old => [...old, id])

            if (!newSession) {
              return
            }

            //Check that the conversation should be stored.
            const me = currentUser()
            let canAddToInbox = false
            switch (topFilter) {
              case TOP_FILTERS.ALL:
                canAddToInbox = true
                break
              case TOP_FILTERS.ASSIGNED_TO_ME:
                canAddToInbox = session.assigned_to === me?.id
                break
              case TOP_FILTERS.TRANSFERS:
                // TODO: No way to check for this yet
                // canAddToInbox = session.transferred_from === myID
                break
              case TOP_FILTERS.OFFLINE:
                // TODO: Add this when offline state exists
                // canAddToInbox = session.state === 'OFFLINE'
                break
              default:
                break
            }

            // if its on convo v2, check inbox and top filter, otherwise add.
            const shouldAdd =
              canAddToInbox && selectedInbox === session.inbox_id

            const { owner_id, user } = newSession
            // Don't add peer chats to conversations
            if (!owner_id && !user) {
              return
            }

            if (userIds.length === 1) {
              // Show notification when not added
              showNotification({
                type: NOTIFICATION_TYPES.NEW_CHAT,
                muteOn: null,
                message: 'Incoming conversation',
                id: newSession.id,
                users: newSession.users,
                name: 'New Conversation',
                msg: newSession,
                v2: true
              })
            }

            if (shouldAdd) {
              // Add session to list
              dispatch(setConversation({ id, conversation: newSession }))
            }
          }

          setNewPush(old => [...old, session.id])
        })
      })
    },
    [
      formatConversation,
      currentUser,
      formatGroupChat,
      showNotification,
      canViewUnassigned,
      dispatch,
      conversations,
      topFilter,
      selectedInbox
    ]
  )

  const handleNotification = React.useCallback(
    msg => {
      const { body, id, src_user_id, source, title } = msg.content

      let name = 'Notification'
      if (src_user_id) {
        name = `|@${src_user_id}| mentioned you`
      } else if (source === 'task') {
        name = title
      }

      showNotification({
        muteOn: null,
        type: NOTIFICATION_TYPES.NOTIFICATION,
        message: body,
        id,
        users: src_user_id ? [src_user_id] : [],
        name,
        msg: msg.content,
        onClick: () => dispatch(markNotificationRead(id))
      })

      dispatch(pushNotification(msg.content))
    },
    [showNotification, dispatch]
  )

  // not used now, probably might come handy in a fuure.
  // const handleReminderEvent = React.useCallback(
  //   msg => {
  //     const { body, title } = msg.content

  //     showNotification({
  //       muteOn: null,
  //       type: NOTIFICATION_TYPES.REMINDER,
  //       message: body,
  //       name: title,
  //       msg: msg.content
  //     })

  //     dispatch(pushNotification(msg.content))
  //   },
  //   [dispatch, showNotification]
  // )

  const handlePatch: SocketContextType['api']['handlePatch'] =
    React.useCallback(
      msg => {
        const { sessionId, content, id, userId, tags } = msg

        const uid = sessionId ?? userId
        const patchMessages = list => {
          if (!(uid in list)) {
            return list
          }

          const patchMessage = (mList = []) => {
            const toPatch = mList.find(message => message.id === id)
            if (toPatch) {
              const newMsg = {
                ...toPatch,
                tags,
                content: content.body,
                meta: { ...(toPatch.meta ?? {}), ...content.meta }
              }
              return [...mList.filter(message => message.id !== id), newMsg]
            } else {
              return mList
            }
          }

          return {
            ...list,
            [uid]: {
              ...list[uid],
              messages: patchMessage(list[uid].messages),
              notes: patchMessage(list[uid].notes)
            }
          }
        }

        dispatch(updateConversations(patchMessages))
        dispatch(insertMessage(msg))
        dispatch(patchMessage(msg))
      },
      [dispatch]
    )

  const handleDelete = React.useCallback(
    msg => {
      const { id } = msg
      dispatch(deleteMessage(id))
    },
    [dispatch]
  )

  const handleAssigned = React.useCallback(
    msg => {
      dispatch(
        updateConversations(old => {
          if (!(msg.sessionId in old)) {
            return old
          }

          const hydrated = hydrateMessage(msg)

          return {
            ...old,
            [msg.sessionId]: {
              ...old[msg.sessionId],
              assigned_to: msg.content.meta.assigned_to,
              messages: [...(old[msg.sessionId].messages ?? []), hydrated]
            }
          }
        })
      )

      dispatch(insertEvent(msg))
    },
    [dispatch]
  )

  const handleVerified = React.useCallback(
    msg => {
      dispatch(
        updateConversations(old => {
          if (!(msg.sessionId in old)) {
            return old
          }

          const hydrated = hydrateMessage(msg)

          return {
            ...old,
            [msg.sessionId]: {
              ...old[msg.sessionId],
              messages: [...(old[msg.sessionId].messages ?? []), hydrated]
            }
          }
        })
      )

      dispatch(insertEvent(msg))
    },
    [dispatch]
  )

  const handleSession = React.useCallback(
    msg => {
      const { session, stamp } = msg

      // Abort this update when object is invalid
      if (!session) {
        console.warn('Unhandled session event', msg)
        return
      }

      session.updatedAt = stamp

      const deleteGroupChat = () => dispatch(removeDM(session.id))
      const addGC = () => dispatch(addGroupChat(session))
      const handleSessionMerge = () => {
        // Determine if a session change causes a session to be hidden from you
        let hide = true

        const { assigned_to, userIds = [] } = session

        if (canViewUnassigned && assigned_to === null) {
          hide = false
        }

        if (canViewOwn) {
          const id = currentUser()?.id
          if (assigned_to === id || userIds.includes(id)) {
            hide = false
          }
        }

        // Remove the session if you shouldn't be able to see it
        if (hide) {
          dispatch(deleteConversation([session.id]))
          return
        }

        const newConvo = formatConversation(session, false)

        if (!newConvo) {
          return
        }

        if (exclude.current.includes(session.id)) {
          return
        }

        const convo = conversations[session.id]
        if (!convo) {
          if (newConvo.ended_at) {
            return
          }

          dispatch(
            updateSingleCovo({
              id: session.id,
              dm: { ...newConvo, isNew: true }
            })
          )
          return
        }

        // Ignore old updates
        if (new Date(stamp) < new Date(convo.lastUpdate)) {
          return
        }

        const base = {
          ...convo,
          ...newConvo
        }

        const wasMoved = convo.owner_id !== session.owner_id
        if (wasMoved) {
          dispatch(
            updateSingleCovo({
              id: session.id,
              dm: {
                ...base,
                messages: [],
                notes: [],
                fetched: false,
                isNew: true
              }
            })
          )
          return
        }

        // TODO: Check what is affected by this
        dispatch(
          updateSingleCovo({
            id: session.id,
            dm: {
              ...base,
              fetched: Boolean(convo.fetched),
              messages: [...(convo.messages ?? []), ...newConvo.messages],
              notes: [...(convo.notes ?? []), ...newConvo.notes]
            }
          })
        )
      }

      // Remove from group chats if it came from there
      if (session.owner_id || session.ownerId) {
        // Now a conversation
        handleSessionMerge()
        deleteGroupChat()
      } else {
        // Now just a group chat
        dispatch(deleteConversation([session.id]))
        addGC()
      }

      // check if it's a name change before push
      if (!dms[session.id]) {
        setNewPush(old => [...old, session.id])
      }
    },
    [
      formatConversation,
      conversations,
      currentUser,
      canViewOwn,
      canViewUnassigned,
      dms,
      dispatch
    ]
  )

  const handleMerge = React.useCallback(
    async msg => {
      dispatch(clearError())

      const {
        data: { fromId, intoId }
      } = msg

      if (fromId === currentUser()?.id) {
        try {
          // Call environments
          const env = await getEnvironment()
          const formatUser = {
            ...env.user,
            settings: env.settings,
            config: env.config
          }
          localStorage.setItem('session_user', JSON.stringify(formatUser))
          setUser(env.user)
        } catch (err) {
          console.error('Error on environments in merge', err)
          dispatch(AddErrorAlert({ err }))
        }

        dispatch(reloadNotifications())
      }

      // Invalidate everything, replace fromId with toId
      propagateMergeUsers(fromId, intoId)
      propagateMergeTasks(fromId, intoId)
      propagateMergeRead(fromId, intoId)
    },
    [
      currentUser,
      setUser,
      propagateMergeTasks,
      propagateMergeUsers,
      propagateMergeRead,
      dispatch
    ]
  )

  const handlePresence = React.useCallback(
    msg => {
      presenceReceived(msg)
    },
    [presenceReceived]
  )

  /**
   * Speculative since the system doesn't actually send these events yet, also only handles conversations
   * @todo Update this when the system uses it
   */
  const handleLeave = React.useCallback(
    async (msg, kicked = false) => {
      const { content, sessionId, id } = msg

      const { user } = content

      // handle parting from peer chats
      if (dms[sessionId]) {
        const session = dms[sessionId]

        dispatch(handlePartKick({ msg, kicked }))
        dispatch(
          handleUpdateUsers({
            users: [
              ...new Set([
                ...dms[sessionId].users.filter(u => u !== content.user.id)
              ])
            ],
            session: sessionId
          })
        )

        // if content.user.id is the current user, need to remove session
        if (currentUser()?.id === user.id) {
          try {
            await removeFromStack(sessionId, 'default')
          } catch (err) {
            console.error('Error leaving conversation', err)
          }

          setNewPush(old => old.filter(session => session !== sessionId))
          dispatch(removeDM(sessionId))
        } else {
          showNotification({
            muteOn: Routes.PEER_CHAT,
            type: NOTIFICATION_TYPES.GROUP,
            message: (
              <i>
                {user.name} {kicked ? 'was booted from' : 'has left'} the chat
              </i>
            ),
            id: msg.id,
            users: session.users,
            name: session.name,
            msg
          })
        }
      } else {
        // Handles leaving client chats
        dispatch(
          updateConversations(convos => {
            if (!(sessionId in convos)) {
              return convos
            }

            // Remove if you were kicked
            if (user.id === currentUser()?.id) {
              const copy = { ...convos }
              delete copy[sessionId]
              return copy
            }

            const newConvo = {
              ...convos[sessionId],
              messages: [
                ...convos[sessionId].messages,
                ...timelineToMessages([msg])
              ]
            }
            newConvo.users = newConvo.users.filter(id => id !== user.id)

            showNotification({
              muteOn: Routes.CLIENT_CHATS,
              type: NOTIFICATION_TYPES.CONVERSATION,
              message: (
                <i>
                  {user.name} {kicked ? 'was booted from' : 'has left'} the chat
                </i>
              ),
              id,
              users: newConvo.users,
              name: newConvo.name,
              msg
            })

            return { ...convos, [sessionId]: newConvo }
          })
        )

        dispatch(insertEvent(msg))
      }
    },
    [showNotification, currentUser, dms, dispatch]
  )

  // Handles events from the socket
  const processEvent = React.useCallback(
    async msg => {
      const { action, action_type } = msg
      if (action === 'join') {
        await handleJoinEvent(msg)
      } else if (action === 'typing') {
        handleTypingEvent(msg)
      } else if (action === 'chat') {
        if (action_type) {
          handleMessageEvent(msg)
        } else {
          handlePatch(msg)
        }
      } else if (action_type === 'note') {
        handleNoteEvent(msg)
      } else if (action === 'inbox_update') {
        await handleInboxUpdateEvent(msg)
      } else if (action === 'notification') {
        handleNotification(msg)
      } else if (action === 'chat.patch') {
        handlePatch(msg)
      } else if (action === 'chat.delete') {
        handleDelete(msg)
      } else if (action === 'survey') {
        handleSurveyEvent(msg)
        dispatch(insertEvent(msg))
      } else if (action === 'session') {
        if (['join', 'add'].includes(action_type)) {
          await handleJoinEvent(msg)
        } else if (['kick', 'part'].includes(action_type)) {
          await handleLeave(msg, action_type === 'kick')
        } else if (action_type === 'assigned') {
          handleAssigned(msg)
        } else if (action_type === 'verified') {
          handleVerified(msg)
        } else {
          handleSession(msg)
        }
      } else if (action === 'merge') {
        await handleMerge(msg)
      } else if (action === 'presence') {
        handlePresence(msg)
      } else if (action === 'kick') {
        await handleLeave(msg, true)
      } else if (action === 'part') {
        await handleLeave(msg)
      } else if (action === 'read') {
        handleUpdateRead(msg)
      } else if (action === 'task') {
        handleTaskEvent(msg)
      } else if (action === 'user') {
        handleUserEvent(msg)
      } else if (action_type === 'reminder') {
        //handleReminderEvent(msg)
      } else {
        console.warn({ UNHANDLED: msg })
      }
    },
    [
      handleJoinEvent,
      handleTypingEvent,
      handleMessageEvent,
      handlePatch,
      handleNoteEvent,
      handleInboxUpdateEvent,
      handleNotification,
      handleDelete,
      handleSurveyEvent,
      dispatch,
      handleLeave,
      handleAssigned,
      handleVerified,
      handleSession,
      handleMerge,
      handlePresence,
      handleUpdateRead,
      handleTaskEvent,
      handleUserEvent
      //handleReminderEvent
    ]
  )

  const handleEvent = React.useCallback(
    msg => {
      dispatch(addEvent(msg))
    },
    [dispatch]
  )

  React.useEffect(() => {
    if (!eventQueue.length) {
      return
    }

    eventQueue.forEach(async msg => {
      try {
        if (!processing.includes(msg.queueID)) {
          dispatch(startProcessing(msg.queueID))
          await processEvent(cloneDeep(msg))
          dispatch(clearEvent(msg))
        }
      } catch (err) {
        console.error(`error in eventqueue`, err)
        dispatch(AddErrorAlert({ err }))
      }
    })
  }, [eventQueue, processing, processEvent, dispatch])

  // Changes the filter
  const handleFilterChange: SocketContextType['api']['handleFilterChange'] =
    React.useCallback((filter, isDirect) => {
      if (isDirect) {
        setSelectedPeerChatFilter(filter)
      }
    }, [])

  // Updates storage with new messages from a fetch
  const handleFetchResult: SocketContextType['api']['handleFetchResult'] =
    React.useCallback(
      (data, extra) => {
        if (!data.length || !extra) {
          return
        }

        const messages = timelineToMessages(data)
        const notes = timelineToNotes(data)

        const { type, id } = extra

        if (type === PAGE_TYPES.DM) {
          dispatch(appendDMs({ id, messages }))
        } else if (type === PAGE_TYPES.CHAT) {
          dispatch(
            updateConversations(convs => {
              const newConv = {
                ...(convs[id] ?? {}),
                messages: [...(convs[id]?.messages ?? []), ...messages],
                notes: [...(convs[id]?.notes ?? []), ...notes]
              }

              return { ...convs, [id]: newConv }
            })
          )
        } else {
          console.error('Unrecognized pagination type', type)
        }
      },
      [dispatch]
    )

  /*
   * START useEffect
   */

  // Checks status of token
  React.useEffect(() => {
    const checkToken = async () => {
      try {
        const token = await getAccessTokenSilently()
        const currentToken = localStorage.getItem('session_token')
        //if token has changed, store the new one and refresh socket connection
        if (token !== currentToken) {
          localStorage.setItem('session_token', token)
          disconnect()
          await connect()
        }
      } catch (err) {
        console.error(
          'Error trying to get and swap token on window focus!',
          err
        )
        dispatch(AddErrorAlert({ err: { message: 'Session expired!' } }))
        // give a few seconds to show error before logging out
        setTimeout(() => {
          window.location.href = `/logout`
        }, 2000)
      }
    }

    if (isFocused && isLoggedIn) {
      checkToken()
    }
  }, [
    connect,
    dispatch,
    getAccessTokenSilently,
    disconnect,
    isFocused,
    isLoggedIn
  ])

  // Loads base inboxes
  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    if (!baseInboxesLoaded) {
      dispatch(loadBaseInboxes())
    }
  }, [isLoggedIn, baseInboxesLoaded, dispatch])

  // Store agents from provider
  React.useEffect(() => {
    if (isLoggedIn) {
      const allAgents = Object.values(getUsers(false))
      const agents = allAgents.filter(({ id }) => id !== currentUser()?.id)
      setAgents(agents)
      setAllAgents(allAgents)
      setAgentsSet(true)
    }
  }, [isLoggedIn, getUsers, currentUser])

  // Set user from storage after being set by the login
  React.useEffect(() => {
    const setSession = async () => {
      const storedUser = JSON.parse(localStorage.getItem('session_user'))
      const token = await getAccessTokenSilently()
      setUserSession(storedUser, token)
    }
    if (isLoggedIn) {
      setSession()
    }
  }, [isLoggedIn, setUserSession, getAccessTokenSilently])

  // Fetches unread notifications
  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    dispatch(loadUnreadNotifications())
  }, [isLoggedIn, dispatch])

  // Handles socket connection and cleanup
  React.useEffect(() => {
    if (isLoggedIn) {
      connect()
    }

    return disconnect

    // Adding connect to this will cause the socket to disconnect and never re-connect
    // eslint-disable-next-line
  }, [isLoggedIn])

  // React to peer chat filter changing
  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    let mounted = true

    if (mounted) {
      dispatch(filterDMs(selectedPeerChatFilter))
      setDoneLoadingDMs(false)
    }
    return () => {
      mounted = false
    }
  }, [isLoggedIn, selectedPeerChatFilter, dispatch])

  // Stores agents as peer chats
  React.useEffect(() => {
    setDoneLoadingDMs(true)
  }, [isLoggedIn, agents, agentsSet, doneLoadingDMs])

  React.useEffect(() => {
    if (connected && allAgents.length > 0) {
      const ids = agents.map(a => a.id)

      // loading presence into provider after receiving from socket
      queryPresence([...ids, currentUser()?.id])
    }
  }, [connected, allAgents, agents, queryPresence, currentUser])

  // Sets up and destroys handlers for the socket
  React.useEffect(() => {
    if (isLoggedIn) {
      registerHandlers(handleEvent)
    }

    return () => {
      deregisterHandlers()
    }
    // eslint-disable-next-line
  }, [isLoggedIn])

  // connected with socket
  React.useEffect(() => {
    if (!connected || queueLoaded || !accessLoaded) {
      return
    }

    // Loading in peer/session stack
    const handleLoadStack = async () => {
      try {
        const stack = await getStack('default')
        saveSessionsFromStack(stack)
        setPeerStack(stack)

        if (stack?.users.length) {
          // fetching timelines
          stack.users.forEach(c => {
            addAgent(c, false)
          })
        }

        if (stack?.sessions.length) {
          stack.sessions.forEach(c => {
            formatGroupChat(c, false)
          })
        }
      } catch (err) {
        console.error('Error loading stack', err)
      }
    }

    dispatch(setQueueLoaded())
    handleLoadStack().catch(err => {
      console.error('Error getting stack', err)
    })
  }, [
    connected,
    queueLoaded,
    accessLoaded,
    saveSessionsFromStack,
    addAgent,
    formatGroupChat,
    dispatch
  ])

  // Load owners if needed
  React.useEffect(() => {
    ownerQueue.forEach(item => {
      if (!allUsers[item.owner]) {
        loadUser(item.owner)
      }
    })
  }, [ownerQueue, allUsers, loadUser])

  // Sets the owner of a session after it has loaded
  React.useEffect(() => {
    if (!ownerQueue.length) {
      return
    }

    const unhandled = []
    const handled = []
    ownerQueue.forEach(item => {
      const { id, owner } = item

      if (!(id in conversations)) {
        return
      }

      const user = allUsers[owner]

      if (!user) {
        unhandled.push(item)
        return
      }

      handled.push({ id, user })
    })

    setOwnerQueue(unhandled)

    setTimeout(() => {
      handled.forEach(({ id, user }) => {
        dispatch(
          updateSingleCovo({
            id,
            dm: {
              ...conversations[id],
              user
            }
          })
        )
      })
    }, 100)
  }, [ownerQueue, allUsers, conversations, dispatch])

  return (
    <SocketContext.Provider
      value={{
        allAgents,
        timeline: { notFound, setNotFound },
        api: {
          typeHandler,
          messageHandler,
          messageHandlerV2,
          endSessions,
          closeSession,
          markSeen,
          noteHandler,
          fetchTimeline,
          formatConversation,
          pushConversation,
          acceptConversation,
          rejectConversation,
          denyConversation,
          denyConversationV2,
          throttledTypingEvent,
          mergeTimeline,
          clearMerge,
          handleFilterChange,
          rejectDM,
          handleMessageEvent,
          handleFetchResult,
          finishFetch,
          fetchSingleSession,
          isClosingSession,
          handlePatch
        },
        retry: {
          msgMap,
          startTimer
        },
        dashboard: {
          currentUser,
          agents,
          allAgents,
          initialConvosList,
          setInitialConvosList,
          error
        },
        peerChat: {
          dms,
          doneLoading: doneLoadingDMs,
          filters: Object.values(PEERCHAT_FILTERS),
          selectedFilter: selectedPeerChatFilter,
          formatDM,
          addAgent,
          convoMap,
          sentMessage,
          resetSentMessage,
          formatGroupChat,
          addGroupSession,
          peerStack,
          setPeerStack,
          newPush,
          setNewPush,
          allGroupChats,
          setAllGroupChats
        }
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

export default SocketProvider

export const useSocket = () => React.useContext(SocketContext)
